import { FormattedMessage, useIntl } from 'react-intl';
import { Platform, ScrollView, StyleSheet } from 'react-native';
import { CustomNavigationHeader, FadeView, theme } from '@sg/garnish';
import { ProductDetails } from '@sg/shared-fe/features/product-details';

import { messages } from '../../ProductDetails.messages.ts';
import {
  type BaseProduct,
  type ProductDetailsMappedContent,
} from '../../ProductDetails.types';
import { ProductNutritionData } from '../ProductNutritionData';

// ─────────────────────────────────────────────────────────────────────────────

export const ProductDetailsReadyXS = (props: Props) => {
  const {
    baseProduct,
    content,
    isUnavailableProduct,
    isAddingToBag,
    navigateBack,
    navigateToLocationSearch,
    navigateToCustomization,
  } = props;

  const { formatMessage } = useIntl();

  // ─────────────────────────────────────────────────────────────────────

  return (
    <FadeView
      show
      duration={theme.transitions.long}
      style={styles.outerContainer}
    >
      <ScrollView
        stickyHeaderIndices={STICKY_HEADER_INDICES}
        contentContainerStyle={styles.contentContainer}
      >
        <CustomNavigationHeader.Container
          palette="kale"
          transition={null}
          safeAreaEdges={Platform.OS === 'android' ? SAFE_AREA_EDGES : null}
        >
          <CustomNavigationHeader.Text
            style={styles.screenTitle}
            sizeMatch={['16']}
          >
            <FormattedMessage {...messages.screenTitle} />
          </CustomNavigationHeader.Text>

          <CustomNavigationHeader.Button.Right
            name="IconClose"
            onPress={navigateBack}
            palette="lighter-semi-transparent"
            accessibilityLabel={formatMessage(messages.goBack)}
          />
        </CustomNavigationHeader.Container>

        <ProductDetails.ContentBlock palette="kale" withTopSpacing={false}>
          <ProductDetails.Header.Small.Container>
            <ProductDetails.Header.Small.ContentContainer>
              <ProductDetails.Title palette="white">
                {baseProduct.name}
              </ProductDetails.Title>

              <ProductDetails.Text palette="white">
                {content.subheading ?? baseProduct.description}
              </ProductDetails.Text>
            </ProductDetails.Header.Small.ContentContainer>

            <ProductDetails.Banner.Small
              productName={baseProduct.name}
              productImageUrl={baseProduct.asset.url}
            />
          </ProductDetails.Header.Small.Container>

          {content.description ? (
            <ProductDetails.Text palette="white">
              {content.description}
            </ProductDetails.Text>
          ) : null}
        </ProductDetails.ContentBlock>

        {content.superIngredients.length > 0 ? (
          <ProductDetails.ContentBlock withBorder>
            <ProductDetails.SectionTitle>
              <FormattedMessage {...messages.superIngredientsTitle} />
            </ProductDetails.SectionTitle>

            <ProductDetails.IngredientsStack>
              {content.superIngredients.map((entry) => (
                <ProductDetails.Ingredient.Container key={entry.name}>
                  <ProductDetails.Ingredient.Image
                    alt={entry.name}
                    baseUrl={entry.image.fields.file.url}
                  />

                  <ProductDetails.Ingredient.ContentContainer>
                    <ProductDetails.Ingredient.Title>
                      {entry.name}
                    </ProductDetails.Ingredient.Title>

                    <ProductDetails.Ingredient.Highlights
                      highlights={entry.highlights}
                    />
                  </ProductDetails.Ingredient.ContentContainer>
                </ProductDetails.Ingredient.Container>
              ))}
            </ProductDetails.IngredientsStack>
          </ProductDetails.ContentBlock>
        ) : null}

        <ProductDetails.ContentBlock>
          <ProductDetails.SectionTitle>
            <FormattedMessage {...messages.nutritionInfoTitle} />
          </ProductDetails.SectionTitle>

          <ProductNutritionData baseProduct={baseProduct} />
        </ProductDetails.ContentBlock>

        {isUnavailableProduct ? (
          <ProductDetails.ContentBlock>
            <ProductDetails.UnavailableProduct />
          </ProductDetails.ContentBlock>
        ) : null}
      </ScrollView>

      {isUnavailableProduct ? (
        <ProductDetails.ChangeLocationButton
          onPress={navigateToLocationSearch}
        />
      ) : null}

      {isUnavailableProduct ? null : (
        <ProductDetails.AddToBagButton
          onPress={navigateToCustomization}
          isLoading={isAddingToBag}
        />
      )}
    </FadeView>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const STICKY_HEADER_INDICES = [0];
const SAFE_AREA_EDGES = ['top'] as const;

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  outerContainer: {
    flex: 1,
  },
  contentContainer: {
    paddingBottom: theme.spacing['25'],
  },
  screenTitle: {
    color: theme.colors.WHITE,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  baseProduct: BaseProduct;
  content: ProductDetailsMappedContent;
  isUnavailableProduct: boolean;
  isAddingToBag: boolean;
  navigateBack: () => void;
  navigateToCustomization: () => void;
  navigateToLocationSearch: () => void;
};
